import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TRACKER_HOST = "https://deals.goshoptoday.com";

/**
 * "Tracker URLs" dialog — opened from the locale chip on an
 * assignment row. Shows one ready-to-copy tracker URL per enabled
 * Amazon storefront. Each URL bakes in `url=https://amazon.XX` so
 * Shopnomix routes the click to the matching country.
 */
function buildLocaleRows(subId, locs, catalog) {
  return (Array.isArray(locs) ? locs : ["amazon.com"]).map((dom) => {
    const meta = catalog.find((l) => l.domain === dom) || { country: dom, flag: "" };
    return {
      ...meta,
      domain: dom,
      url: `${TRACKER_HOST}/g/${subId}?url=https://${dom}&cid=UNIQUE_YOUR_CLICK_ID&source=homepage`,
    };
  });
}

export default function ViewLocalesDialog({ assignment, onClose, localeCatalog }) {
  if (!assignment) return null;
  const rows = buildLocaleRows(
    assignment.publisher_sub_id || "",
    assignment.enabled_locales,
    localeCatalog,
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="sn-view-locales-dialog">
        <DialogHeader>
          <DialogTitle>Tracker URLs for {assignment.publisher_email}</DialogTitle>
          <DialogDescription>
            Canonical per-locale tracker URLs. Each one bakes <code className="font-mono">url=https://amazon.XX</code> into the redirect, so Shopnomix lands the user on the matching storefront.
            <span className="block mt-1 font-mono text-[10px] text-slate-500">Sub ID · {assignment.publisher_sub_id || "—"}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="sn-view-locales-list">
          {rows.map((row) => (
            <div key={row.domain} className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{row.flag}</span>
                  <span className="font-bold text-sm text-slate-900">{row.country}</span>
                  <span className="font-mono text-[10px] text-slate-500">· {row.domain}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(row.url);
                    toast.success(`Copied · ${row.domain}`);
                  }}
                  data-testid={`sn-view-locale-copy-${row.code || row.domain}`}
                >
                  <DownloadSimple size={12} className="mr-1" /> Copy
                </Button>
              </div>
              <Input value={row.url} readOnly className="font-mono text-[11px] bg-white" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
